import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { WindowRef } from '@shared/modules/window-ref/window-ref';
import { environment } from '@src/environments';
import { External, LogError, LogInfo } from '@store/actions';
import { CookieService } from 'ngx-cookie-service';
import {
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs';

import { getConfigClientFromRTE } from '../../../config/config-client';
import * as medalliaActions from '../../actions/medallia/medallia.actions';

declare var KAMPYLE_ONSITE_SDK: any;

@Injectable()
export class MedalliaEffects {
  private onMedalliaSDKLoaded: Observable<void>;

  constructor(
    private actions$: Actions,
    private windowRef: WindowRef,
    private cookieService: CookieService,
  ) {
    const subject = new ReplaySubject<void>();
    this.windowRef.nativeWindow.addEventListener('neb_OnsiteLoaded', () => subject.next());
    this.onMedalliaSDKLoaded = subject;
  }

  setInfoForSurvey$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(medalliaActions.SetInfoForSurvey),
        tap(() => {
          const spoid = this.cookieService.get('cl_spoid');
          this.windowRef.nativeWindow['spoid'] = spoid ? JSON.parse(spoid) : -1;
          this.windowRef.nativeWindow['randomNum'] = Math.floor(Math.random() * 4) + 1;
        }),
      ),
    { dispatch: false },
  );

  showSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(medalliaActions.ShowSurvey),
      switchMap((action) => {
        return forkJoin([
          of(action.payload),
          this.onMedalliaSDKLoaded.pipe(
            take(1),
            timeout(3000),
            catchError(() => {
              // If the SDK does not load within 3 seconds, we return an error observable to trigger the forkJoin
              return of({ error: 'Medallia SDK timeout' });
            }),
          ),
        ]).pipe(
          map(([survey_info, sdkStatus]) => {
            // If the SDK did not load on time, we throw an error
            if (sdkStatus && typeof sdkStatus === 'object' && 'error' in sdkStatus) {
              throw new Error(sdkStatus.error);
            }
            try {
              const config = getConfigClientFromRTE();
              KAMPYLE_ONSITE_SDK.showForm(config.medallia_surveys[survey_info.survey_name]);
              return LogInfo({
                payload: { eventId: 'medallia-survey-success', message: 'Medallia survey shown' },
              });
            } catch (error) {
              throw new Error(error.message);
            }
          }),
          catchError((error) => {
            return [
              LogError({
                payload: {
                  eventId: 'medallia-survey-failure',
                  message: 'Error showing Medallia survey',
                  error,
                },
              }),
              External({ url: environment.dashboardLink }),
            ];
          }),
        );
      }),
    ),
  );
}
